import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { isSupabaseConnected } from '../lib/supabase';

const Step = ({ num, title, children }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
      {num}
    </div>
    <div>
      <p className="font-bold text-gray-800 text-sm">{title}</p>
      <div className="text-sm text-gray-600 mt-1 space-y-1">{children}</div>
    </div>
  </div>
);

const Code = ({ children }) => (
  <code className="bg-gray-100 border border-gray-200 text-blue-700 px-2 py-0.5 rounded font-mono text-xs break-all">
    {children}
  </code>
);

const SupabaseSetupGuide = () => {
  const [open, setOpen] = useState(!isSupabaseConnected);

  if (isSupabaseConnected) return null;

  return (
    <div className="max-w-2xl mx-auto mt-4 px-4">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-left hover:bg-amber-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-amber-500" />
          <span className="font-bold text-amber-800 text-sm">Supabase not connected — click to see setup guide</span>
        </div>
        <SafeIcon icon={open ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-amber-600" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-white border border-gray-200 rounded-b-xl px-5 py-5 space-y-5 shadow-sm">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-sm font-bold text-red-700 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiAlertCircle} /> Fix for 404 Error in Project Settings
                </p>
                <p className="text-xs text-red-600 mt-1">
                  The 404 error happens when your Supabase URL is incorrect. Follow the steps below carefully.
                </p>
              </div>

              <Step num="1" title="Go to your Supabase Dashboard">
                <p>Open <a href="https://supabase.com/dashboard" target="_blank" rel="noreferrer" className="text-blue-600 underline font-semibold">supabase.com/dashboard</a> and select your <strong>"video recorder"</strong> project.</p>
              </Step>

              <Step num="2" title='Click "Project Settings" → "API"'>
                <p>In the left sidebar, click <strong>⚙️ Project Settings</strong>, then click the <strong>API</strong> tab.</p>
              </Step>

              <Step num="3" title="Copy your Project URL (exact format matters!)">
                <p>Under <strong>Project URL</strong>, copy the value. It must look exactly like:</p>
                <div className="mt-1">
                  <Code>https://abcdefghijklmnop.supabase.co</Code>
                </div>
                <p className="mt-1 text-red-600 text-xs font-semibold">
                  ⚠️ Do NOT add /rest/v1 or any path — just the base URL ending in .supabase.co
                </p>
              </Step>

              <Step num="4" title="Copy your anon/public API key">
                <p>Under <strong>Project API keys</strong>, copy the <strong>anon public</strong> key (starts with <Code>eyJ...</Code>).</p>
                <p className="text-xs text-red-600 font-semibold mt-1">⚠️ Do NOT use the service_role key here.</p>
              </Step>

              <Step num="5" title="Add keys to your .env file">
                <p>In your project root, open (or create) <Code>.env</Code> and add:</p>
                <div className="mt-2 bg-gray-900 text-green-400 rounded-lg px-4 py-3 font-mono text-xs space-y-1">
                  <p>VITE_SUPABASE_URL=https://your-project-id.supabase.co</p>
                  <p>VITE_SUPABASE_ANON_KEY=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...</p>
                </div>
              </Step>

              <Step num="6" title="Run the migration SQL in Supabase">
                <p>Go to <strong>SQL Editor</strong> in Supabase and run the migration from:</p>
                <Code>src/supabase/migrations/1776757253782-create_user_profiles.sql</Code>
              </Step>

              <Step num="7" title="Restart your dev server">
                <p>Stop the server (<Code>Ctrl+C</Code>) and restart with <Code>npm run dev</Code> to pick up the new .env values.</p>
              </Step>

              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <p className="text-xs text-green-700 font-semibold flex items-center gap-1">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-500" />
                  After setup, the navbar will show <strong>☁️ Supabase</strong> (green) instead of 🗄️ Local.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupabaseSetupGuide;